<script lang="ts">
	import { Background } from "@components/Background.js";
	import BookMeeting from "@components/BookMeeting.svelte";
	import FaqItems from "@components/FaqItems.svelte";
	import FoldingCards from "@components/FoldingCards.svelte";
	import InfoCard from "@components/InfoCard.svelte";
	import LogoReferences from "@components/LogoReferences.svelte";
	import ReferenceContents from "@components/ReferenceContents.svelte";
	import Section from "@components/Section.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import arrowNext from "@core/assets/icons/arrow-next.svg?raw";
	import { PartnerPlacement } from "@core/schema/PartnerPlacement.js";
	import { ReferencePlacement } from "@core/schema/ReferencePlacement.js";
	import { getConnectWithFastestPath } from "@core/schema/paths/getConnectWithFastestPath.js";
	import { getReferencePath } from "@core/schema/paths/getReferencePath.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import { getClientsTypicalProblems } from "../data/getClientsTypicalProblems.js";
	import { getFaqsClients } from "../data/getFaqsClients.js";
	import { getPartnerLogos } from "../data/getPartnerLogos.js";
	import { getReasonsForCoop } from "../data/getReasonsForCoop.js";
	import type { ClientsProps } from "./ClientsProps.js";

	const { tenant } = getGlobalTemplateProps();
	const { partners }: ClientsProps = $props();
	const anchorReferences = "reference";
</script>

<Layout>
	<Section
		paddingFollowNext
		background={Background.LightGradient}
		centered
		gridHeadline="container-6"
		gridDesc="container-6"
		hero
	>
		{#snippet headline()}
			<Formatted text="cg[[Spoléhají na nás]] malé firmy i světoznámé značky" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="co[[wm[[Komu pomáháme zrychlit růst podnikání?]]]] nl[[]] Získali jsme si důvěru u podniků napříč Českem a obory"
				/>
			</p>
		{/snippet}
	</Section>
	<LogoReferences logos={getPartnerLogos(partners, PartnerPlacement.Basic)} />
	<div class="flex flex-col mt-8 lg:mt-12 gap-3">
		<Link
			variant={ButtonVariant.Phlox}
			class="mx-auto flex items-center justify-center"
			href={getConnectWithFastestPath(tenant)}
			>Chci fastest služby
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
		<Link
			variant={ButtonVariant.GreenOutlineLightBg}
			class="mx-auto flex items-center justify-center"
			href="#{anchorReferences}"
			>Zobrazit reference
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</div>

	<Section id="reference" gridHeadline="container-8" centered>
		{#snippet headline()}
			<Formatted text="V cg[[portfoliu]] máme cukrárnu, Volvo i advokátní kancelář" />
		{/snippet}
		<div class="mt-12">
			<ReferenceContents swapReversed limit={8} />
		</div>
		<div class="flex flex-col mt-8 lg:mt-12 gap-3">
			<Link
				variant={ButtonVariant.GreenOutlineLightBg}
				class="mx-auto flex items-center justify-center"
				href={getReferencePath(tenant)}
			>
				Zobrazit více
				<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
			</Link>
			<Link
				variant={ButtonVariant.Phlox}
				class="mx-auto flex items-center justify-center"
				href={getConnectWithFastestPath(tenant)}
				>Chci fastest služby
				<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
			</Link>
		</div>
	</Section>

	<Section background={Background.LightGradient} noPadding="both" gridHeadline="container-6 lg:text-center">
		{#snippet headline()}
			<Formatted text="S čím cg[[za námi]] klienti často chodí" />
		{/snippet}
	</Section>
	<Section gridChildren="max-md:full-width" background={Background.White} noPadding="both">
		<div
			class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 max-lg:full-width lg:px-1 bg-fstlight-100 border-t-4 border-fstlight-100"
		>
			{#each getClientsTypicalProblems(tenant) as { title, desc, iconName, items }, index (index)}
				<InfoCard {title} {desc} {iconName} {items} />
			{/each}
		</div>
	</Section>
	<Link
		variant={ButtonVariant.Phlox}
		class="flex items-center justify-center mx-auto mt-8 lg:mt-12"
		href={getConnectWithFastestPath(tenant)}
	>
		<Formatted text="Chci fastest služby" />
		<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
	</Link>

	<Section centered gridHeadline="container-6">
		{#snippet headline()}
			<Formatted text="Odpovědi na cg[[časté otázky]]" />
		{/snippet}
		<FaqItems faqs={getFaqsClients(tenant)} />
	</Section>

	<Section
		background={Background.Onyx}
		gridHeadline="container-6 lg:container-start-1 lg:container-end-5 xl:container-start-2 xl:container-end-4 max-lg:text-center"
		gridDesc="lg:container-start-7 lg:container-end-12 xl:container-start-6 xl:container-end-11"
	>
		{#snippet headline()}
			<Formatted text="cg[[Proč]] si plácnout s cg[[Fastest Solution]]" />
		{/snippet}
		{#snippet desc()}
			<FoldingCards color={Background.Onyx} cards={getReasonsForCoop(tenant)} />
		{/snippet}
		<div class="container-12">
			<Link
				variant={ButtonVariant.Phlox}
				class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
				href={getConnectWithFastestPath(tenant)}
				>Chci fastest služby
				<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
			</Link>
		</div>
	</Section>

	{@const contacts = getBookMeetingPeople(tenant)}
	<BookMeeting contacts={[contacts[BookMeetingPerson.Kucera], contacts[BookMeetingPerson.Svejda]]} />
</Layout>
